import { typographyTokens } from '../../theme/typographyTokens';
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Divider,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  FormControlLabel,
  useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import StepPage from '../../components/CreateTask/CreateTask';

//types
import { TaskExtended, TaskStatus } from '../../../shared/types/Task';
import { SEPPhase } from '../../../shared/types/SEP';
import ModalWidget from '../../components/ModalWidget/ModalWidget';
import { useGetSepTasksQuery, useGetSepQuery } from '../../services/sepSlice';
import { useGetUserQuery } from '../../services/usersSlice';
import { useAccount } from '@azure/msal-react';
import { env } from '../../../shared/env';
import { isDependentTaskHidden } from '../../utils/shared';
import TaskListTable from '../../components/Tables/TaskListTable';
import Switch from '../../components/Switch';

/** Task view type */
type TaskViewType = 'all' | 'my';

/** Styled Toggle Button for ALL TASKS / MY TASKS */
const StyledToggleButton = styled(ToggleButton)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: typographyTokens.fontWeights.semibold,
  fontSize: typographyTokens.fontSizes.body,
  padding: '6px 16px',
  border: `1px solid ${theme.palette.prime.blue}`,
  color: theme.palette.prime.blue,
  '&.Mui-selected': {
    backgroundColor: theme.palette.prime.blue,
    color: theme.palette.content.white,
    '&:hover': {
      backgroundColor: theme.palette.prime.blue,},
  },
  '&:hover': {
    backgroundColor: 'rgba(35, 114, 185, 0.1)',
  },
}));

interface TaskListProps {
  sepID: number;
  filterTasks: boolean;
  filterOthers: boolean;
  hideOthers: boolean;
  handleHide: () => void;
  handleTasksSwitch: () => void;
  handleCompletedSwitch: () => void;
  onNewTaskAdded: (newTask: TaskExtended) => void;
  hidePendingTasks?: boolean
  hideFilterChange?: () => void
  hidePendingOtherTasks?: boolean
  hideFilterChangeOthers?: () => void
}

export const TaskList = ({
  sepID,
  filterTasks,
  filterOthers,
  hideOthers,
  handleHide,
  handleTasksSwitch,
  handleCompletedSwitch,
  onNewTaskAdded,
  hidePendingTasks,
  hideFilterChange,
  hidePendingOtherTasks,
  hideFilterChangeOthers
}: TaskListProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [taskView, setTaskView] = useState<TaskViewType>('all');
  const { data: tasks } = useGetSepTasksQuery(sepID);
  const { data: sep } = useGetSepQuery(sepID);
  const { data: loggedInUser } = useGetUserQuery('me');
  const account = useAccount();
  const isComplete = sep?.phase === SEPPhase.complete;
  const isAdmin = env.REACT_APP_LOCAL_DEV_ADMIN === 'true' || account?.idTokenClaims?.roles?.includes('AuthSuperUser');
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  /** Handle task view toggle change */
  const handleViewChange = (
    _event: React.MouseEvent<HTMLElement>,
    newView: TaskViewType | null
  ) => {
    if (newView !== null) {
      setTaskView(newView);
    }
  };
  /** The enabled non-complete tasks assigned to the logged-in user */
  const nonCompleteUserAssignedTasks = React.useMemo(() => {
    return (
      tasks?.filter(        
        (e: TaskExtended) =>
          e.enabled &&
          e.status !== TaskStatus.complete && e.status !== TaskStatus.cancelled &&
          e.assignedUserID === loggedInUser?.id && isDependentTaskHidden(e)
      ) || []
    );
  }, [tasks, loggedInUser?.id]);

  const pendingUserAssignedTasks = React.useMemo(() => {
    return (
      tasks?.filter(
        (e: TaskExtended) =>
          e.enabled &&
          e.status !== TaskStatus.pending &&
          e.assignedUserID === loggedInUser?.id
          && isDependentTaskHidden(e)
      ) || []
    );
  }, [tasks, loggedInUser?.id]);
  /** The enabled non-complete non-pending tasks assigned to the logged-in user */
  const nonCompleteNonPendingUserAssignedTasks = React.useMemo(() => {
    return nonCompleteUserAssignedTasks?.filter(
      (e: any) => e.status !== TaskStatus.pending
    );
  }, [nonCompleteUserAssignedTasks]);
  
  /** My Tasks - tasks assigned to the logged-in user with filters applied */
  const myTasks = React.useMemo(() => {
    if(hidePendingTasks && filterTasks) {
      return nonCompleteUserAssignedTasks?.filter((e:TaskExtended) => e.status !== TaskStatus.pending && isDependentTaskHidden(e))
    } else if(hidePendingTasks) {
      return pendingUserAssignedTasks
    } else if(filterTasks) {
      return nonCompleteUserAssignedTasks
    } else {
      return tasks?.filter((e: TaskExtended) => e.enabled && e.assignedUserID === loggedInUser?.id && isDependentTaskHidden(e))
    }
  }, [hidePendingTasks, filterTasks, pendingUserAssignedTasks, nonCompleteUserAssignedTasks, tasks, loggedInUser?.id]);

  /** All Tasks - all enabled tasks with filters applied */
  const allTasks = React.useMemo(() => {
    let result = tasks?.filter((e: TaskExtended) => e.enabled && isDependentTaskHidden(e)) || [];
    
    if (hidePendingTasks) {
      result = result.filter((e: TaskExtended) => e.status !== TaskStatus.pending);
    }
    if (filterTasks) {
      result = result.filter((e: TaskExtended) => 
        e.status !== TaskStatus.complete && e.status !== TaskStatus.cancelled
      );
    }
    return result;
  }, [tasks, hidePendingTasks, filterTasks]);

  /** Current displayed tasks based on view toggle */
  const displayedTasks = taskView === 'all' ? allTasks : myTasks;

  return (
    <>
      <ModalWidget
        isOpen={isOpen}
        title="Add Task"
        styleObject={{ color: isDarkMode ? theme.palette.content.white : theme.palette.content.default }}
        children={
          <StepPage
            isInit={!isOpen}
            sepID={sepID}
            onFinish={(newTask: TaskExtended) => {
              onNewTaskAdded(newTask);
              setIsOpen(false);
            }}
          />
        }
        onClose={() => setIsOpen(false)}
      />

      {/* Header Row: Toggle | Filters | Create Button */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          px: 3,
          mx: { xs: 0, md: 3 },
          py: 3,
        }}
      >
        {/* Left: ALL TASKS / MY TASKS Toggle */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: { xs: 1, md: 2 },
          }}
        >
          <Typography sx={{ font: 'normal normal bold 16px/24px Open Sans;', mb: 1, color: isDarkMode ? theme.palette.content.white : theme.palette.content.default }}>
              View&nbsp;:
            </Typography>
          <ToggleButtonGroup
          value={taskView}
          exclusive
          onChange={handleViewChange}
          aria-label="task view toggle"
          size="small"
        >
          <StyledToggleButton value="all" aria-label="all tasks" sx={{ color: isDarkMode ? theme.palette.content.white : theme.palette.content.default }}>
            All Tasks
          </StyledToggleButton>
          <StyledToggleButton value="my" aria-label="my tasks" sx={{ color: isDarkMode ? theme.palette.content.white : theme.palette.content.default }}>
            My Tasks
          </StyledToggleButton>
        </ToggleButtonGroup>
        {/* Hide Locked Tasks Toggle */}
          <FormControlLabel
            control={<Switch checked={hidePendingTasks} onChange={hideFilterChange} />}
            label={
              <Typography sx={{ font: 'normal normal normal 14px/22px Open Sans;', mr: 1, color: isDarkMode ? theme.palette.content.white : theme.palette.content.default }}>
                Hide Locked
              </Typography>
            }
            labelPlacement="start"
            sx={{ ml: 0 }}
          />

          {/* Hide Completed Tasks Toggle */}
          <FormControlLabel
            control={<Switch checked={filterTasks} onChange={handleTasksSwitch} />}
            label={
              <Typography sx={{ font: 'normal normal normal 14px/22px Open Sans;', mr: 1, color: isDarkMode ? theme.palette.content.white : theme.palette.content.default }}>
                Hide Completed
              </Typography>
            }
            labelPlacement="start"
            sx={{ ml: 0 }}
          />
        </Box>
        

        {/* Right: Filters and Create Button */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: { xs: 1, md: 2 },
          }}
        >


          {/* Create New Task Button */}
          {(!isComplete || isAdmin) && (
            <Button
              variant="outlined"
              size="small"
              sx={{
                '&.MuiButton-outlined': {
                  border: `2px solid ${theme.palette.prime.blue}`,
                  color: theme.palette.prime.blue,
                  textTransform: 'none',
                  fontWeight: typographyTokens.fontWeights.bold,
                  ml: 1,
                },
              }}
              onClick={() => setIsOpen(true)}
            >
              CREATE NEW TASK
            </Button>
          )}
        </Box>
      </Box>

      <Divider />

      {/* Task Table */}
      <TaskListTable
        sepID={sepID}
        tasks={displayedTasks || []}
        isMyTask={taskView === 'my'}
        sepPhase={sep?.phase}
      />
    </>
  );
};
